/**
 * PRD.253 — Sync Health helpers.
 *
 * Pure (non-React) helpers for deriving sync-health state, capturing local
 * upload errors, and recording retry attempts. All persistence is in
 * localStorage so a support bundle can be built even after a reload.
 *
 * NOTE: stack traces are captured ONLY for the support bundle — never render
 * them to clinic users (see PRD §8).
 */

// ─── Derived sync state ───

export type SyncHealthState = 'connected' | 'syncing' | 'offline' | 'stale' | 'stuck' | 'unknown';

// ─── Thresholds (PRD §7.3) ───

export const SYNC_STALE_MINUTES = 15;
export const SYNC_STUCK_MINUTES = 30;
/**
 * Grace period before a transient upload error / non-draining queue is treated
 * as "stuck" (and the global stuck-sync modal is shown). The oldest pending op
 * must have been waiting at least this long, so a brief `Failed to fetch` blip
 * that recovers on its own never nags the user. Tune this to change how soon the
 * modal appears.
 */
export const SYNC_STUCK_SIGNAL_MINUTES = 2;
export const MAX_RECENT_UPLOAD_ERRORS = 20;
export const MAX_RETRY_LOGS = 20;

// ─── localStorage keys ───

const UPLOAD_ERRORS_KEY = 'powercare_upload_errors';
const RETRY_LOGS_KEY = 'powercare_retry_logs';

// ─── Captured-error / retry shapes ───

export interface LocalUploadErrorEntry {
  timestamp: string;
  writeUrl?: string;
  operationCount?: number;
  error: {
    message: string;
    name?: string;
    stack?: string;
  };
}

export interface SyncRetryLogEntry {
  timestamp: string;
  /** What kind of recovery action was attempted. */
  action: 'retry' | 'force-resync' | 'auto-force-resync';
  pendingBefore?: number;
  pendingAfter?: number;
  outcome: 'started' | 'success' | 'error';
  detail?: string;
}

// ─── Input snapshot for deriving state ───

export interface SyncStateInput {
  /** PowerSync reports a live connection to the sync service. */
  connected: boolean;
  /** Browser navigator.onLine — the device has network at all. */
  online: boolean;
  /** Currently downloading or uploading. */
  inFlight: boolean;
  /** Number of CRUD ops waiting to upload. */
  pendingCount: number;
  /** ISO timestamp of the oldest pending op, if known. */
  oldestPendingAt?: string | null;
  /** ISO/locale string of the last successful sync, if known. */
  lastSyncedAt?: string | null;
  /** Count of recent captured upload errors. */
  recentErrorCount: number;
  /** Did the most recent retry fail to drain the queue? */
  retryDidNotDrain?: boolean;
  /**
   * An active sync error reported by PowerSync (download/websocket OR upload).
   * Captures the "can't reach the sync service at all" case (e.g. bad sync URL,
   * blocked endpoint) where no upload error is ever recorded because no
   * connection was established.
   */
  hasSyncError?: boolean;
}

// ─── localStorage helpers ───

function readJsonArray<T>(key: string): T[] {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as T[]) : [];
  } catch {
    return [];
  }
}

function writeJsonArray<T>(key: string, value: T[]): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Best-effort — storage may be full or disabled.
  }
}

/**
 * Capture a single upload failure locally. Keeps only the most recent
 * MAX_RECENT_UPLOAD_ERRORS entries (newest first).
 */
export function recordUploadError(entry: LocalUploadErrorEntry): void {
  const next = [entry, ...getRecentUploadErrors()].slice(0, MAX_RECENT_UPLOAD_ERRORS);
  writeJsonArray(UPLOAD_ERRORS_KEY, next);
}

/** Read recent upload errors (newest first). */
export function getRecentUploadErrors(): LocalUploadErrorEntry[] {
  return readJsonArray<LocalUploadErrorEntry>(UPLOAD_ERRORS_KEY);
}

/** Clear captured upload errors (e.g. after a successful drain). */
export function clearUploadErrors(): void {
  try {
    localStorage.removeItem(UPLOAD_ERRORS_KEY);
  } catch {
    // ignore
  }
}

/** Append a retry/force-resync log entry (newest first, capped). */
export function recordRetryLog(entry: SyncRetryLogEntry): void {
  const next = [entry, ...getRetryLogs()].slice(0, MAX_RETRY_LOGS);
  writeJsonArray(RETRY_LOGS_KEY, next);
}

/** Read retry logs (newest first). */
export function getRetryLogs(): SyncRetryLogEntry[] {
  return readJsonArray<SyncRetryLogEntry>(RETRY_LOGS_KEY);
}

// ─── Derivation ───

function minutesSince(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  return (Date.now() - then) / 60_000;
}

/**
 * Derive a single sync-health state from a snapshot.
 *
 * Priority order (PRD §7.1 / §7.3):
 *  - offline   → no network
 *  - stuck     → online, pending ops + (errors / didn't drain / oldest > stuck threshold)
 *  - stale     → online, pending ops sitting past the stale threshold
 *  - syncing   → actively transferring
 *  - connected → live link, queue empty (or draining cleanly)
 *  - unknown   → not enough signal
 */
export function deriveSyncHealthState(input: SyncStateInput): SyncHealthState {
  if (!input.online) return 'offline';

  const oldestMin = minutesSince(input.oldestPendingAt);
  const hasPending = input.pendingCount > 0;

  if (hasPending) {
    const stuckByAge = oldestMin != null && oldestMin >= SYNC_STUCK_MINUTES;
    const stuckBySignal =
      input.recentErrorCount > 0 || input.retryDidNotDrain === true || input.hasSyncError === true;
    // Online + a non-draining queue + an active failure signal = stuck. We do
    // NOT require a live PowerSync connection: a device that can't connect at
    // all (bad sync URL, blocked endpoint) while holding pending writes is the
    // most stuck of all. The stuck-sync MODAL additionally waits a grace period
    // (useSyncStatus + SYNC_STUCK_SIGNAL_MINUTES) so transient blips don't nag.
    if (stuckByAge || stuckBySignal) {
      return 'stuck';
    }
    const staleByAge = oldestMin != null && oldestMin >= SYNC_STALE_MINUTES;
    if (staleByAge) return 'stale';
  }

  if (input.inFlight) return 'syncing';
  if (input.connected) return 'connected';
  return 'unknown';
}

/**
 * True when a sync error is an expired-auth/JWT failure — the session token
 * lapsed so the device stopped receiving updates and silently shows stale data.
 * PowerSync surfaces this as code `PSYNC_S2103` ("JWT has expired"); we also
 * match looser token-expiry phrasings defensively.
 */
export function isAuthExpiredSyncError(message: string | null | undefined): boolean {
  if (!message) return false;
  return /PSYNC_S2103|jwt[^a-z]*(has )?expired|token[^a-z]*(has )?expired|expired token/i.test(
    message,
  );
}

/**
 * True when sync is caught in a re-open loop. After a sync-rules change the
 * server bumps the checkpoint version; a client whose local state can't
 * reconcile tears down and re-opens the stream repeatedly — surfacing as
 * `ConnectionClosedError: Connection is busy re-opening` (and aborted
 * in-flight checkpoint fetches). It never settles, so the page silently shows
 * stale data. Recovery is a local clear + re-sync (Force Resync).
 */
export function isReopenLoopError(message: string | null | undefined): boolean {
  if (!message) return false;
  return /busy re-?opening|ConnectionClosedError/i.test(message);
}

/**
 * True for the POISONED SHARED-WORKER connection.
 *
 * `@powersync/web` keeps ONE SQLite connection per db filename inside a
 * SharedWorker (`WASQLiteDB.worker.js`), shared by every tab AND the sync
 * worker. A single orphaned `BEGIN EXCLUSIVE` — whose ROLLBACK also failed, a
 * failure the SDK swallows — poisons that connection permanently: every
 * subsequent apply throws "cannot start a transaction within a transaction",
 * the checkpoint never commits, and download progress snaps back forever.
 *
 * The SharedWorker OUTLIVES page reloads, so F5, Retry Sync and Force Resync
 * all fail — Force Resync clears the database and then reconnects to the same
 * poisoned connection. The only recovery is closing EVERY tab on this origin
 * (or quitting the browser) so the worker is torn down.
 *
 * Confirmed twice on live orgs. Because no automated recovery can fix it from
 * inside the page, this matcher exists purely to TELL the user what to do.
 */
export function isPoisonedWorkerError(message: string | null | undefined): boolean {
  if (!message) return false;
  return /cannot start a transaction within a transaction/i.test(message);
}

/** Recovery instruction for {@link isPoisonedWorkerError}. */
export const POISONED_WORKER_TITLE = 'Close all tabs to finish syncing';
export const POISONED_WORKER_BODY =
  'This browser tab shares one database connection with your other tabs, and that ' +
  'connection needs a clean restart. Reloading or retrying will not clear it.\n\n' +
  'Close EVERY tab for this site (or quit the browser), then open one tab and let ' +
  'the sync finish without reloading.';

/** How long the re-open loop must persist before we treat it as genuinely stuck. */
export const SYNC_REOPEN_STUCK_MS = 25_000;

// ─── Auto download-recovery (persistent fetch-transport failure) ───

/**
 * Cross-browser matcher for a fetch transport failure MESSAGE. Used only as a
 * compatibility fallback — prefer the structured {@link isTransportDownloadError}
 * (error name), which is locale-independent.
 * Chrome: "Failed to fetch" · Firefox: "NetworkError when attempting to fetch
 * resource" · Safari: "Load failed" · misc: "network request failed".
 */
export function isFetchTransportError(message?: string | null): boolean {
  return /Failed to fetch|NetworkError|Load failed|network request failed/i.test(message ?? '');
}

/**
 * True when a PowerSync download error is a network/transport failure (the sync
 * stream couldn't reach the server), as opposed to an HTTP/auth/coded error.
 *
 * Structured-first: browser `fetch()` transport failures throw a `TypeError`
 * across engines, so the error NAME is a locale-independent signal. PowerSync
 * auth/coded errors (e.g. `PSYNC_S2103` JWT expiry) are NOT `TypeError`s, so
 * this cleanly excludes them (they're owned by SyncReauthModal). The message
 * regex is a compatibility fallback only.
 */
export function isTransportDownloadError(
  name: string | null | undefined,
  message: string | null | undefined,
): boolean {
  if (name === 'TypeError') return true;
  return isFetchTransportError(message);
}

/** A persistent download transport error must hold this long before we act. */
export const DOWNLOAD_FETCH_STUCK_MS = 30_000;

/** At most this many silent auto force-resyncs per rolling window (loop guard). */
export const AUTO_FORCE_RESYNC_MAX_PER_WINDOW = 1;
export const AUTO_FORCE_RESYNC_WINDOW_MS = 30 * 60_000;
/** After a reload, wait this long before the manual fallback modal may appear. */
export const AUTO_FORCE_RESYNC_POST_RELOAD_GRACE_MS = 60_000;
/** How long a tab's auto-force lock is considered live (crash-safe TTL). */
export const AUTO_FORCE_LOCK_TTL_MS = 60_000;

// Browser-wide (localStorage) so the cooldown/lock are SHARED across tabs and
// survive a reload. A per-tab sessionStorage guard could not prevent a cross-tab
// or post-reload force-resync loop — and forceResync() clears the multi-tab
// SHARED database, so uncoordinated tabs would churn.
const AUTO_FORCE_LOG_KEY = 'powercare_auto_force_resync_log';
const AUTO_FORCE_LOCK_KEY = 'powercare_auto_force_resync_lock';

export interface AutoForceResyncLock {
  ownerTabId: string;
  startedAt: number;
  expiresAt: number;
}

/** Auto-force timestamps within the active window (older entries pruned out). */
export function getAutoForceResyncTimes(now: number = Date.now()): number[] {
  return readJsonArray<number>(AUTO_FORCE_LOG_KEY).filter(
    (t) => typeof t === 'number' && now - t < AUTO_FORCE_RESYNC_WINDOW_MS,
  );
}

/** True while still under the per-window cap — i.e. an auto-force is allowed. */
export function canAutoForceResync(now: number = Date.now()): boolean {
  return getAutoForceResyncTimes(now).length < AUTO_FORCE_RESYNC_MAX_PER_WINDOW;
}

/** Record an auto-force. Call immediately BEFORE forceResync so it survives the reload. */
export function recordAutoForceResync(now: number = Date.now()): void {
  writeJsonArray(AUTO_FORCE_LOG_KEY, [...getAutoForceResyncTimes(now), now]);
}

/**
 * True when an auto force-resync happened very recently (within `withinMs`).
 * Read once on a fresh page load to distinguish a *recovery* reload (softened
 * "Restoring your data…" copy) from a genuine first-time sync — the marker is
 * written immediately before the reload, so on load it is only seconds old. A
 * real first sync has no marker.
 */
export function wasRecentAutoForceResync(
  withinMs: number = AUTO_FORCE_RESYNC_POST_RELOAD_GRACE_MS,
  now: number = Date.now(),
): boolean {
  const times = getAutoForceResyncTimes(now);
  if (times.length === 0) return false;
  return now - Math.max(...times) <= withinMs;
}

function readAutoForceLock(): AutoForceResyncLock | null {
  try {
    const raw = localStorage.getItem(AUTO_FORCE_LOCK_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as AutoForceResyncLock;
    return parsed && typeof parsed.ownerTabId === 'string' ? parsed : null;
  } catch {
    return null;
  }
}

/** True when another tab currently holds a live recovery lock. */
export function autoForceLockHeldByOther(tabId: string, now: number = Date.now()): boolean {
  const cur = readAutoForceLock();
  return !!cur && cur.expiresAt > now && cur.ownerTabId !== tabId;
}

/**
 * Attempt to claim the browser-wide recovery lock for this tab. Returns false if
 * another tab holds a live lock. Uses write-then-reread to settle races; the
 * shared cooldown cap is the ultimate backstop against a double force.
 */
export function tryAcquireAutoForceLock(tabId: string, now: number = Date.now()): boolean {
  if (autoForceLockHeldByOther(tabId, now)) return false;
  const lock: AutoForceResyncLock = {
    ownerTabId: tabId,
    startedAt: now,
    expiresAt: now + AUTO_FORCE_LOCK_TTL_MS,
  };
  try {
    localStorage.setItem(AUTO_FORCE_LOCK_KEY, JSON.stringify(lock));
  } catch {
    return false;
  }
  return readAutoForceLock()?.ownerTabId === tabId;
}

/** Release the lock if this tab owns it (or it's already gone). */
export function releaseAutoForceLock(tabId: string): void {
  const cur = readAutoForceLock();
  if (!cur || cur.ownerTabId === tabId) {
    try {
      localStorage.removeItem(AUTO_FORCE_LOCK_KEY);
    } catch {
      // ignore
    }
  }
}

/** Human-readable label + tone for a sync-health state. */
export function describeSyncHealthState(state: SyncHealthState): {
  label: string;
  tone: 'ok' | 'warn' | 'error' | 'neutral';
} {
  switch (state) {
    case 'connected':
      return { label: 'Connected', tone: 'ok' };
    case 'syncing':
      return { label: 'Syncing', tone: 'neutral' };
    case 'offline':
      return { label: 'Offline', tone: 'warn' };
    case 'stale':
      return { label: 'Stale', tone: 'warn' };
    case 'stuck':
      return { label: 'Stuck', tone: 'error' };
    default:
      return { label: 'Unknown', tone: 'neutral' };
  }
}
